import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/format/datetime";

import {
  type SecretSummary,
  useDeleteSecret,
  useSecrets,
  useUpsertSecret,
} from "./use-secrets";

/**
 * 大項目10「Secrets・認証連携」（管理）。
 *
 * ここで扱うのは「BPM が外部システムを叩くための鍵」だけで、ユーザーの
 * ログイン認証（Zeus IdP）とは別レイヤー。値は API が返さないので画面にも出さない。
 */
export function SecretsPage() {
  const { t } = useTranslation("common");
  const secrets = useSecrets();
  const upsert = useUpsertSecret();
  const remove = useDeleteSecret();

  // 編集中の対象。null = 何も編集していない、"new" = 新規追加
  const [editing, setEditing] = useState<SecretSummary | "new" | null>(null);

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold">{t("secrets.title")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("secrets.description")}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setEditing("new")}
          disabled={upsert.isPending}
        >
          {t("secrets.add")}
        </Button>
      </header>

      <p className="bg-muted text-muted-foreground rounded-md px-3 py-2 text-xs">
        {t("secrets.scopeNote")}
      </p>

      {editing === null ? null : (
        <SecretForm
          target={editing}
          isPending={upsert.isPending}
          onCancel={() => setEditing(null)}
          onSubmit={(key, value) => {
            upsert.mutate(
              editing === "new"
                ? { key, value }
                : { key: editing.key, value, existingId: editing.id },
            );
            setEditing(null);
          }}
        />
      )}

      {secrets.isPending ? <Skeleton className="h-32 w-full" /> : null}

      {secrets.isError ? (
        <p className="text-destructive text-sm">{t("state.error")}</p>
      ) : null}

      {secrets.data ? (
        secrets.data.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("secrets.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-muted/50 text-muted-foreground text-left text-xs">
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t("secrets.key")}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t("secrets.value")}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t("secrets.owner")}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t("secrets.updatedAt")}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {secrets.data.map((secret) => (
                  <tr
                    key={secret.id}
                    className="hover:bg-muted/40 border-border border-b last:border-0"
                  >
                    <td className="px-3 py-2 font-mono text-xs">
                      {secret.key}
                    </td>
                    <td className="text-muted-foreground px-3 py-2 text-xs">
                      {t("secrets.hidden")}
                    </td>
                    <td className="px-3 py-2">{secret.owner ?? "—"}</td>
                    <td className="text-muted-foreground px-3 py-2 tabular-nums">
                      {formatDateTime(secret.updatedAt)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing(secret)}
                        >
                          {t("secrets.update")}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => remove.mutate(secret.key)}
                        >
                          {t("secrets.remove")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">
          {t("secrets.authentications")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("secrets.noAuthentications")}
        </p>
      </section>
    </section>
  );
}

/**
 * 鍵の登録・更新フォーム。
 *
 * `window.prompt` は使わない（値がマスクされないため、肩越しや画面共有で
 * 見えてしまう）。値の入力は `type="password"`。
 * 更新のときはキーを変えられない（別の鍵になってしまう）ので表示だけにする。
 */
function SecretForm({
  target,
  isPending,
  onCancel,
  onSubmit,
}: {
  readonly target: SecretSummary | "new";
  readonly isPending: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (key: string, value: string) => void;
}) {
  const { t } = useTranslation("common");
  const isNew = target === "new";
  const [key, setKey] = useState(isNew ? "" : target.key);
  const [value, setValue] = useState("");

  const canSave = key.trim() !== "" && value !== "" && !isPending;

  return (
    <form
      className="border-border flex flex-col gap-3 rounded-md border p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSave) return;
        onSubmit(key.trim(), value);
      }}
    >
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">{t("secrets.key")}</span>
        {isNew ? (
          <input
            value={key}
            onChange={(event) => setKey(event.target.value)}
            placeholder={t("secrets.keyPrompt")}
            autoComplete="off"
            spellCheck={false}
            className="border-border bg-background focus-visible:ring-ring rounded-md border px-3 py-2 font-mono text-xs focus-visible:ring-2 focus-visible:outline-none"
          />
        ) : (
          <input
            value={key}
            readOnly
            className="border-border bg-muted text-muted-foreground rounded-md border px-3 py-2 font-mono text-xs"
          />
        )}
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">{t("secrets.value")}</span>
        <input
          type="password"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={t("secrets.valuePrompt")}
          autoComplete="new-password"
          className="border-border bg-background focus-visible:ring-ring rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
        />
      </label>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("actions.cancel")}
        </Button>
        <Button type="submit" disabled={!canSave}>
          {t("actions.save")}
        </Button>
      </div>
    </form>
  );
}
